import { PermissionDto } from '../../../../api-client';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flexbox, Icon, TextField, Typography } from '@hiiretail/synergy-ui';
import { useIntl } from 'react-intl';
import { ProfileMessages } from '../../Profile.messages';
import { Spinner } from '../../../../common/components/Spinner/Spinner';
import styled from 'styled-components';
import { FuseOptions } from 'fuse.js';
import { useSearch } from '../../../../common/hooks/useSearch/useSearch';
import { Retry } from '../../../../common';
import { useAuth } from '@hiiretail/core';

type MyPermissionsListProps = {
  permissions: PermissionDto[];
  isLoading: boolean;
  refetchPermissions: () => void;
  error: unknown;
};

type MyPermissionsListComponent = React.FC<MyPermissionsListProps>;

const MyPermissionsListWrapper = styled(Flexbox)`
  box-shadow: 0 1px 4px rgb(0 0 0 / 8%), 0 0 2px rgb(0 0 0 / 3%);
  padding: ${({ theme }) => theme.spacing(4)}px;
  overflow: hidden;
`;

export const MyPermissionsList: MyPermissionsListComponent = ({
  permissions,
  isLoading,
  error,
  refetchPermissions,
}) => {
  const intl = useIntl();
  const { currentUser } = useAuth();
  const [token, setToken] = useState('');

  const getToken = async () => {
    const res = await currentUser?.getIdToken();
    setToken(res || '');
  };

  const searchOptions: FuseOptions<PermissionDto> = useMemo(
    () => ({
      keys: ['id', 'description', 'alias'],
      threshold: 0.1,
      caseSensitive: false,
      findAllMatches: true,
    }),
    [],
  );

  const { filteredData: filteredPermissions, onSearchChange } = useSearch(
    permissions,
    searchOptions,
  );

  useEffect(() => {
    getToken().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MyPermissionsListWrapper
      container
      direction="column"
      style={{ height: '100%', width: '100%' }}
    >
      <Flexbox item container alignItems="center" gutter={2}>
        <Flexbox item container alignItems="center">
          <div onClick={() => navigator.clipboard.writeText(token)}>
            <Icon.Locker size="md" />
          </div>
        </Flexbox>
        <Flexbox item>
          <Typography>{intl.formatMessage(ProfileMessages.myPermissions)}</Typography>
        </Flexbox>
      </Flexbox>
      <Box mb={4} />
      {error ? (
        <Retry text="permissions" handleClick={refetchPermissions} />
      ) : (
        <>
          {isLoading && <Spinner dataTestId="my-permissions-spinner" />}
          {!isLoading && (
            <>
              <Flexbox item>
                <TextField
                  name="effective-permissions-search"
                  data-testid="effective-permissions-search"
                  label=""
                  hideLabel
                  fullWidth
                  onChange={(event) => onSearchChange(event.target.value)}
                  disabled={permissions.length < 1}
                  placeholder="Find permissions"
                />
              </Flexbox>
              <Box mb={4} />

              {!permissions.length && (
                <Flexbox container item alignItems="center" justifyContent="center" flex="1 1 auto">
                  <Typography color={() => 'rgb(0,0,0,0.54)'}>
                    You don't have any permissions
                  </Typography>
                </Flexbox>
              )}
              {permissions.length && filteredPermissions.length ? (
                <Flexbox item container direction="column" style={{ overflowY: 'auto' }} gutter={2}>
                  {filteredPermissions.map((permission) => (
                    <Flexbox item container key={permission.id} direction="column">
                      <Flexbox item container gutter={2} alignItems="center">
                        <Flexbox item>
                          <Typography variant="body1" weight="bold">
                            {permission.id}
                          </Typography>
                        </Flexbox>
                        <Flexbox item>
                          <Typography variant="caption" color={() => 'rgb(0,0,0,0.54)'}>
                            {`(${permission.alias || 'No alias'})`}
                          </Typography>
                        </Flexbox>
                      </Flexbox>
                      <Flexbox item>
                        <Typography variant="caption">
                          {permission.description || 'No description'}
                        </Typography>
                      </Flexbox>
                    </Flexbox>
                  ))}
                </Flexbox>
              ) : null}
              {permissions.length && !filteredPermissions.length ? (
                <Flexbox container item alignItems="center" flex="1 1 auto">
                  <Typography color={() => 'rgb(0,0,0,0.54)'}>
                    No permissions found, try changing the filter
                  </Typography>
                </Flexbox>
              ) : null}
            </>
          )}
        </>
      )}
    </MyPermissionsListWrapper>
  );
};
